import saveAs from 'file-saver';
import { Parser } from 'json2csv';
import {
  get,
  map,
  isEmpty,
  join,
  isArray,
  reduce,
  values,
  startCase
} from 'lodash';
import moment from 'moment';
import html2canvas from 'html2canvas';
import XLSX from 'xlsx';

import { formatDateRangeLabel } from './dates';
import lookerApi from '../services/looker-api';

const API_ROOT = process.env.REACT_APP_PORTAL_API;
const MAX_WIDTH = 32768;
const MAX_AREA = 268435456;
const IMAGE_MAX_SIZE_ERROR =
  'There are too many entries in this query. Please refine filters or use .csv or .xls format for export.';
const IMAGE_ERROR =
  'Something unexpected happened. Please try again later. I the problem persists please refine filters or use .csv or .xls format for export.';

const getImageFromProxy = nodeImg => {
  return new Promise((resolve, reject) => {
    const url = `${API_ROOT}v1/embed-image?url=${nodeImg.src}`;
    const img = new Image();
    img.setAttribute('class', nodeImg.getAttribute('class'));
    img.onload = () => {
      nodeImg.replaceWith(img);
      resolve(img);
    };
    img.onerror = () => reject(`Failed to load image's URL: ${url}`);
    img.src = url;
  });
};

export const downloadReportFunc = (view, filters, queryMeta, sortBy) => (
  api,
  resultFormat,
  title
) =>
  api(
    {
      view,
      filters,
      queryMeta,
      sorts: sortBy
    },
    resultFormat,
    title
  );

const getAllImagesFromProxy = async newElement => {
  if (newElement) {
    const nodes = newElement.getElementsByTagName('img');
    if (nodes) {
      const promises = [];
      for (let i = 0; i < nodes.length; i += 1) {
        if (
          /^https?:\/\/.+cloudfront.+\.(jpg|png|gif|jpeg)$/g.test(nodes[i].src)
        ) {
          promises.push(getImageFromProxy(nodes[i]));
        }
      }
      await Promise.all(promises);
    }
  }
};

export const downloadQuery = async (
  id,
  querymeta,
  filters,
  resultFormat,
  title
) => {
  try {
    const data = await lookerApi.runInlineQuery(
      id,
      querymeta,
      filters,
      resultFormat
    );

    const blob = await data.blob();
    saveAs(blob, `${title}.${resultFormat}`);
    return true;
  } catch (e) {
    console.log(e);
    return false;
  }
};

export const exportCVMiUsers = async (data, filename, fields) => {
  if (isEmpty(data)) {
    return '';
  }

  const prefix = `CVMi Users - ${moment().format('MMMM Do YYYY')}`;

  const json2csvParser = new Parser({
    fields
  });
  const dataCSV = json2csvParser.parse(data);

  const blob = new Blob([`${prefix}\n\n${dataCSV}`], { type: 'text/csv' });
  saveAs(blob, filename);
};

export const generateFileName = (prefix = 'report', extension = '') =>
  `${prefix}${extension}`;

export const downloadCSV = (data, prefix = 'download', onDone = null) => {
  const splitArrayTemp = data.split('\n');
  const columnCount = splitArrayTemp[2].split(',').length;

  const arrayOfArrayCsv = data
    .split('\n')
    .map((row, index) => {
      // Skip empty rows (like the blank line)
      if (row.trim() === '') return null;

      // Split the row by commas
      const splitRow = row.split(',');

      // If the number of split elements exceeds the column count, assume extra commas are in the first field
      if (splitRow.length > columnCount) {
        // Combine the extra parts of the first field
        const firstField =
          '"' +
          splitRow
            .slice(0, splitRow.length - columnCount + 1)
            .join(',')
            .replace(/"/g, '""') +
          '"';

        const otherFields = splitRow.slice(splitRow.length - columnCount + 1);
        return [firstField, ...otherFields];
      } else {
        return splitRow;
      }
    })
    .filter(row => row !== null);

  const filename = generateFileName(prefix, '.csv');
  const blob = new Blob([arrayOfArrayCsv.join('\n')], { type: 'text/csv' });
  saveAs(blob, filename);
  if (onDone) {
    onDone();
  }
};

export const downloadXLS = (data, prefix = 'download', onDone = null) => {
  const blob = new Blob([data], { type: 'text/csv' });
  const filename = generateFileName(prefix, '.xls');
  saveAs(blob, filename);
  if (onDone) {
    onDone();
  }
};

export const downloadCSV2XLS = (data, prefix = 'download', onDone = null) => {
  const splitArrayTemp = data.split('\n');
  const columnCount = splitArrayTemp[2].split(',').length;

  const arrayOfArrayCsv = data
    .split('\n')
    .map((row, index) => {
      // Skip empty rows (like the blank line)
      if (row.trim() === '') return null;

      // Split the row by commas
      const splitRow = row.split(',');

      // If the number of split elements exceeds the column count, assume extra commas are in the first field
      if (splitRow.length > columnCount) {
        // Combine the extra parts of the first field
        const firstField = splitRow
          .slice(0, splitRow.length - columnCount + 1)
          .join(',');
        const otherFields = splitRow.slice(splitRow.length - columnCount + 1);
        return [firstField, ...otherFields];
      } else {
        return splitRow;
      }
    })
    .filter(row => row !== null);

  const wb = XLSX.utils.book_new();
  const newWs = XLSX.utils.aoa_to_sheet(arrayOfArrayCsv);
  XLSX.utils.book_append_sheet(wb, newWs);
  const fileName = generateFileName(prefix, '.xlsx');
  XLSX.writeFile(wb, fileName);
  if (onDone) {
    onDone();
  }
};

export const convertObjectToCSV = objArray => {
  var array = typeof objArray != 'object' ? JSON.parse(objArray) : objArray;
  if (array.length === 0) {
    return '';
  }
  var str = String(Object.keys(array[0])) + '\r\n';

  for (var i = 0; i < array.length; i++) {
    var line = '';
    for (var index in array[i]) {
      if (line !== '') line += ',';

      line += array[i][index];
    }

    str += line + '\r\n';
  }
  return str;
};

export const downloadPNG = (
  element,
  prefix = 'download',
  onDone = null,
  onError = null
) => {
  if (
    !element ||
    element.scrollHeight > MAX_WIDTH ||
    element.scrollWidth > MAX_WIDTH ||
    element.scrollHeight * element.scrollWidth > MAX_AREA
  ) {
    onError(IMAGE_MAX_SIZE_ERROR);
    return;
  }

  html2canvas(element, {
    onclone: getAllImagesFromProxy,
    useCORS: true,
    logging: false
  })
    .then(canvas => {
      if (canvas.width === 0 || canvas.height === 0) {
        console.error('Canvas is empty');
        if (onError) {
          onError(IMAGE_ERROR);
        }
      } else {
        const imageData = canvas.toDataURL('image/png');
        const blob = imageData.replace(
          /^data:image\/png/,
          'data:application/octet-stream'
        );
        saveAs(blob, generateFileName(prefix, '.png'));
        if (onDone) {
          onDone();
        }
      }
    })
    .catch(error => {
      console.error(error);
      if (onError) {
        onError(IMAGE_ERROR);
      }
    });
};

export const exportFilters = (filters = []) => {
  if (isEmpty(filters)) {
    return '';
  }

  const order = [
    'show',
    'season',
    'episode',
    'age',
    'gender',
    'country',
    'date'
  ];

  const parseField = field => {
    const fieldValue = get(field, 'value', {});
    return {
      Filter: field.label,
      Value: isArray(fieldValue)
        ? join(map(fieldValue, value => value.label, ','))
        : fieldValue.label
    };
  };

  const fields = reduce(
    order,
    (result, f) => {
      const field = get(filters, ['fields', f], {});
      return isEmpty(field.value) ? result : [...result, parseField(field)];
    },
    []
  );

  const json2csvParser = new Parser({ fields: ['Filter', 'Value'] });
  const csv = json2csvParser.parse(fields);

  return csv;
};

export const exportCustomReport = fieldLabels => (data, filters = {}) => {
  if (isEmpty(data)) {
    return '';
  }

  const prefix = `TV Time - TVLytics - ${moment().format('MMMM Do YYYY')}`;
  const filtersCSV = isEmpty(filters) ? '' : `\n\n${exportFilters(filters)}`;

  const opts = { fields: fieldLabels };
  const json2csvParser = new Parser(opts);
  const dataCSV = json2csvParser.parse(data);

  return `${prefix}${filtersCSV}\n\n${dataCSV}`;
};

export const exportReport = (data, filters = {}) => {
  if (isEmpty(data)) {
    return '';
  }

  const prefix = `TV Time - TVLytics - ${moment().format('MMMM Do YYYY')}`;
  const filtersCSV = isEmpty(filters) ? '' : `\n\n${exportFilters(filters)}`;

  const json2csvParser = new Parser({ header: false });
  const dataCSV = json2csvParser.parse(data);

  return `${prefix}${filtersCSV}\n\n${dataCSV}`;
};

export const exportReportWithHeaders = (data, filters = {}) => {
  if (isEmpty(data)) {
    return '';
  }

  const prefix = `TV Time - TVLytics - ${moment().format('MMMM Do YYYY')}`;
  const filtersCSV = isEmpty(filters) ? '' : `\n\n${exportFilters(filters)}`;

  const json2csvParser = new Parser({ header: true, quote: '' });
  const dataCSV = json2csvParser.parse(data);

  return `${prefix}${filtersCSV}\n\n${dataCSV}`;
};

export const exportComments = (data, filters = {}) => {
  if (isEmpty(data)) {
    return '';
  }

  const filtersCSV = exportFilters(filters);

  const parsedFields = ['content', 'language', 'nb_likes', 'nb_comments'];

  const parsedData = map(data, row => ({
    content: get(row, 'content', ''),
    language: get(row, 'language', ''),
    nb_likes: get(row, 'nb_likes', ''),
    nb_comments: get(row, 'total_comments', '')
  }));

  const json2csvParser = new Parser({ fields: parsedFields });
  const dataCSV = json2csvParser.parse(parsedData);

  const prefix = `TV Time - TVLytics - ${moment().format('MMMM Do YYYY')}`;
  const possibleFilters = isEmpty(filters) ? '' : `\n\n${filtersCSV}`;

  return `${prefix}${possibleFilters}\n\n${dataCSV}`;
};

export const formatNaturalLanguageFilters = (filters, addSource = true) => {
  const langMap = {
    age: val => `${val} only.`,
    gender: val => `${startCase(val)} only.`,
    country: val => `${val.split(',').join(', ')} viewers.`,
    panel: val => `${val} sample.`,
    title: val => `${val}.`,
    show: val => `${val}.`,
    movie: val => `${val}.`,
    contentType: val => `${val} only.`,
    genre: val => `${val.split(',').join(', ')} titles only.`,
    network: val => `${val.split(',').join(', ')} titles only.`,
    studio: val => `${val.split(',').join(', ')} titles only.`,
    originating_country: val =>
      `${val.split(',').join(', ')} originated titles only.`,
    originating_language: val => `${val} originated titles only.`,
    status: val => `${val} titles.`,
    runtime: val => `${val} runtime only.`,
    first_release_date: date =>
      `First released between ${formatDateRangeLabel(date, 'and')}`,
    date: date => formatDateRangeLabel(date, '-'),
    report_date: date => formatDateRangeLabel(date, '-'),
    days_from_release: val => `${val} days from release.`,
    releaseDate: rangeArray => {
      const array = JSON.parse(rangeArray);
      return `${array[0]} to ${array[1]} days from release.`;
    },
    titleId: () => ''
  };

  const filterOrder = [
    'title',
    'show',
    'movie',
    'contentType',
    'genre',
    'network',
    'studio',
    'originating_country',
    'originating_language',
    'status',
    'runtime',
    'first_release_date',
    'metric',
    'age',
    'gender',
    'country',
    'panel',
    'days_from_release',
    'releaseDate',
    'titleId',
    'date',
    'report_date'
  ];
  const string = values(filters)
    .filter(obj => obj.value)
    .sort((a, b) => filterOrder.indexOf(a.id) - filterOrder.indexOf(b.id))
    .map(obj => {
      const val = Array.isArray(obj.value)
        ? join(
            map(obj.value, o => o.label || o.value),
            ','
          )
        : obj.value.label;

      if (!val) {
        return '';
      }

      const naturalLanguageOperator = langMap[obj.id];

      if (!naturalLanguageOperator) {
        return val;
      }

      return naturalLanguageOperator(val);
    })
    .join(' ');

  return addSource ? `Source: Whip Media CVMi. ${string}` : string;
};

export const exportXLSX = async (blob, filters, sheetName, fileName) => {
  const array = await blob.text();
  const workbook = XLSX.read(array, { type: 'binary' });
  const jsonData = XLSX.utils.sheet_to_json(
    workbook.Sheets[workbook.SheetNames[0]]
  );
  const wb = XLSX.utils.book_new();
  const ws = XLSX.utils.json_to_sheet([[filters]], {
    skipHeader: true
  });
  XLSX.utils.sheet_add_json(ws, jsonData, { origin: 'A3' });
  XLSX.utils.book_append_sheet(wb, ws, sheetName);
  XLSX.writeFile(wb, fileName);
};

export const exportXLSXBuffer = async (blob, filters, sheetName, fileName) => {
  const array = await blob.arrayBuffer();
  const workbook = XLSX.read(array, { type: 'array' });
  const jsonData = XLSX.utils.sheet_to_json(
    workbook.Sheets[workbook.SheetNames[0]]
  );
  const wb = XLSX.utils.book_new();
  const ws = XLSX.utils.json_to_sheet([[filters]], {
    skipHeader: true
  });
  XLSX.utils.sheet_add_json(ws, jsonData, { origin: 'A3' });
  XLSX.utils.book_append_sheet(wb, ws, sheetName);
  XLSX.writeFile(wb, fileName);
};

export const exportCSV = async (response, filters, filename) => {
  const dataCSV = await response.text();
  const result = `\n"${filters}"\n\n${dataCSV}`;
  const blob = await new Blob([result], {
    type: 'text/csv'
  });
  saveAs(blob, filename);
};

export const exportRaw = async (blob, filename) => {
  saveAs(blob, filename);
};

export const downloadTableQuery = async (spec, type, prefix) => {
  try {
    const { limit, sorts, view, queryMeta, filters } = spec;

    const viewToEntity = {
      movieAudienceAffinityTrack: 'movie',
      movieAffinityFollower: 'movie',
      showReportAffinityOR: 'series',
      anticipationFollowerAffinity: 'series'
    };

    const entityValue = viewToEntity[spec.view];

    const detailAffinityContentTypeFilter = {
      contentType: {
        id: 'contentType',
        value: { value: entityValue }
      }
    };

    const res = await lookerApi.runInlineQuery(
      view,
      queryMeta,
      { ...filters, ...detailAffinityContentTypeFilter },
      type,
      {
        sorts,
        limit
      }
    );
    const mmddyyyy = moment().format('MMDDYYYY');
    const filename = generateFileName(
      `cvmi_${view}_${
        prefix ? `${prefix.replaceAll(' ', '')}_` : ''
      }${mmddyyyy}`,
      `.${type}`
    );
    const natLangFilters = formatNaturalLanguageFilters(filters);

    if (type === 'xlsx') {
      exportXLSX(await res.blob(), natLangFilters, view, filename);
      return true;
    } else if (type === 'csv') {
      exportCSV(res, natLangFilters, filename);
      return true;
    }

    const blob = await res.blob();
    saveAs(blob, filename);
    return true;
  } catch (e) {
    console.log(e);
    return false;
  }
};
